import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import theme from '../theme';
import {
  Alert, Box, Chip, CircularProgress, FormControl, Paper,
  Snackbar, Tab, Tabs, Typography,
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { grey } from '@mui/material/colors';
import api from '../services/api';
import moment from "moment";
import 'moment/locale/pt-br';
import TabReconhecimento from './TabReconhecimento';
import TabProcedimento from './TabProcedimento';
import TabContatos from './TabContatos';
import TabChamado from './TabChamado';
import TabWazuh from './TabWazuh';

const QUICK_SUGGESTIONS = [
  "Equipe ciente, monitorando",
  "Verificado, sem impacto",
  "Em tratamento",
  "Contato com cliente realizado",
  "Escalonado para N2",
];

export default function FormDialog(props) {
  const [handlerSnack, setHandlerSnack] = React.useState(false);
  const [acks, setAcks] = React.useState([]);
  const [escalations, setEscalations] = React.useState([]);
  const [hostOverview, setHostOverview] = React.useState(null);
  const [overviewLoading, setOverviewLoading] = React.useState(false);
  const [overviewDays, setOverviewDays] = React.useState(7);
  const [tabIndex, setTabIndex] = React.useState(0);

  // Athena configs
  const [athenaConfigs, setAthenaConfigs] = React.useState([]);

  const isBulk = Array.isArray(props.eventId);

  const item = {
    description: '',
    cod_empresa: props.codEmpresa,
    event_ids: props.eventId,
    detail: props.detailAlarms,
    source: props.source || 'zabbix',
  };
  const [state, setState] = React.useState(item);

  const idIgnore = [0, 123456789];
  const handleChange = (event) => {
    const { name, value } = event.target;
    setState(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Viewing indicator — notifica outros operadores que este dialog está aberto
  React.useEffect(() => {
    if (!props.open) return;

    const eventIds = Array.isArray(props.eventId) ? props.eventId : [props.eventId];
    eventIds.forEach(eid => {
      window.dispatchEvent(new CustomEvent('viewing-send', {
        detail: { type: 'viewing_start', event_id: eid, cod_empresa: props.codEmpresa }
      }));
    });

    return () => {
      eventIds.forEach(eid => {
        window.dispatchEvent(new CustomEvent('viewing-send', {
          detail: { type: 'viewing_stop', event_id: eid, cod_empresa: props.codEmpresa }
        }));
      });
    };
  }, [props.open, props.eventId, props.codEmpresa]);

  React.useEffect(() => {
    if (!props.open) return;
    setState(item);
    setHostOverview(null);
    setEscalations([]);
    setOverviewDays(7);
    setTabIndex((props.source || 'zabbix') === 'wazuh' && !isBulk ? 2 : 0);
    getAcks();
    if (!isBulk) {
      fetchHostOverview(7);
      fetchEscalations();
    }
  }, [props.codEmpresa, props.open, props.eventId]);

  const fetchHostOverview = (days) => {
    if (!props.codEmpresa || (!props.hostname && !props.ip)) return;
    setOverviewLoading(true);
    const params = new URLSearchParams();
    if (props.hostname) params.append('hostname', props.hostname);
    if (props.ip) params.append('ip', props.ip);
    params.append('days', days);
    if (props.tagsPpi && props.tagsPpi > 0) params.append('tags_ppi', props.tagsPpi);
    if (props.detailAlarms) params.append('alarm_name', props.detailAlarms);
    if (props.source) params.append('source', props.source);
    if (props.tags) params.append('tags', props.tags);

    api.get(`dashboard/host-overview/${props.codEmpresa}?${params.toString()}`)
      .then(res => setHostOverview(res.data))
      .catch(() => setHostOverview(null))
      .finally(() => setOverviewLoading(false));
  };

  const getAcks = () => {
    if (!props.codEmpresa || typeof props.eventId === 'object') {
      setAcks([]);
      return;
    }
    api.get(
      idIgnore.includes(props.eventId) ? `dashboard/ack_other/${props.eventId}/${props.codEmpresa}/${props.hostid}` :
        `dashboard/acks/${props.codEmpresa}/${props.eventId}/`)
      .then(res => setAcks(res.data))
      .catch(() => setState(item));
  };

  const fetchEscalations = () => {
    if (!props.codEmpresa || !props.eventId) return;
    api.get(`escalation/find/${props.eventId}/${props.codEmpresa}`)
      .then(res => setEscalations(res.data))
      .catch(() => setEscalations([]));
  };

  // Athena configs
  React.useEffect(() => {
    if (!props.open || !props.codEmpresa) return;
    setAthenaConfigs([]);

    const fetchEmpresa = api.get(`dashboard/athena-config/${props.codEmpresa}`).then(r => r.data || []).catch(() => []);
    const fetchDefault = props.codEmpresa !== 0
      ? api.get('dashboard/athena-config/0').then(r => r.data || []).catch(() => [])
      : Promise.resolve([]);

    Promise.all([fetchEmpresa, fetchDefault]).then(([empresaConfigs, defaultConfigs]) => {
      setAthenaConfigs([...empresaConfigs, ...defaultConfigs]);
    });
  }, [props.open, props.codEmpresa]);

  const [msgSnack, setMsgSnack] = React.useState('');
  const [sevSnack, setSevSnack] = React.useState('');
  const showNotify = (msg, sev = "success") => {
    setMsgSnack(msg);
    setSevSnack(sev);
    setHandlerSnack(true);
  };

  const handlerClose = () => {
    props.handlerClose();
  };

  const handleSubmit = () => {
    if (idIgnore.includes(props.eventId)) {
      api.post('dashboard/ack_other/', {
        hosts: [props.hostid],
        cod_empresa: props.codEmpresa,
        description: state.description,
        severity: props.severity,
      }).then(() => {
        showNotify("Vistado com sucesso.", "success");
        handlerClose();
      }).catch((err) => {
        console.log(err);
        showNotify("Um erro ocorreu...", 'error');
      });
    } else {
      api.post('dashboard/acks/', state)
        .then(() => {
          showNotify("Vistado com sucesso.", "success");
          handlerClose();
        }).catch((err) => {
          console.log(err);
          showNotify("Um erro ocorreu...", 'error');
        });
    }
  };

  const handleSuggestionClick = (text) => {
    setState(prev => ({
      ...prev,
      description: prev.description ? prev.description + '\n' + text : text,
    }));
  };

  // Bulk: layout simples sem abas
  if (isBulk) {
    return (
      <div>
        <Snack open={handlerSnack} message={msgSnack} severity={sevSnack} handlerSnack={() => setHandlerSnack(false)} />
        <Dialog open={props.open} fullWidth maxWidth='md'>
          <Box sx={{ mt: 1 }}>
            <DialogTitle>Reconhecimento em massa ({props.eventId.length} alarmes)</DialogTitle>
            <DialogContent>
              <Tabs
                value={tabIndex} onChange={(_, v) => setTabIndex(v)}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Reconhecimento" />
                <Tab label="Chamado" disabled={athenaConfigs.length === 0} />
              </Tabs>

              {tabIndex === 0 && (
                <>
                  <FormControl fullWidth>
                    <TextField
                      inputProps={{ maxLength: 255 }}
                      multiline rows={6} required autoFocus
                      margin="dense" id="description" name="description"
                      label="Detalhes:" type="text" fullWidth
                      value={state.description} onChange={handleChange}
                      variant="outlined" size="small"
                    />
                  </FormControl>
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {QUICK_SUGGESTIONS.map((s, i) => (
                      <Chip key={i} label={s} size="small" variant="outlined" onClick={() => handleSuggestionClick(s)} sx={{ cursor: 'pointer' }} />
                    ))}
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" disabled={!state.description} onClick={handleSubmit}>Reconhecer</Button>
                  </Box>
                </>
              )}

              {tabIndex === 1 && (
                <TabChamado
                  props={props}
                  state={state}
                  setState={setState}
                  showNotify={showNotify}
                  athenaConfigs={athenaConfigs}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button sx={{ color: theme.palette.text.primary }} onClick={handlerClose}>Cancelar</Button>
            </DialogActions>
          </Box>
        </Dialog>
      </div>
    );
  }

  // Single alarm: layout com abas (Wazuh tem tab extra)
  const isWazuh = (props.source || 'zabbix') === 'wazuh';

  return (
    <div>
      <Snack open={handlerSnack} message={msgSnack} severity={sevSnack} handlerSnack={() => setHandlerSnack(false)} />
      <Dialog open={props.open} fullWidth maxWidth='lg' scroll="paper">
        <Box>
          <DialogTitle sx={{ pb: 0 }}>
            Reconhecimento: {props.hostname || '—'} - {props.detailAlarms}
          </DialogTitle>

          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Reconhecimento" />
            <Tab label="Procedimento" />
            {isWazuh && <Tab label="Wazuh" />}
            <Tab label="Contatos & Empresa" />
            <Tab label="Chamado" disabled={athenaConfigs.length === 0} />
          </Tabs>

          <DialogContent dividers sx={{ minHeight: 400 }}>
            {tabIndex === 0 && (
              <TabReconhecimento
                props={props}
                state={state}
                handleChange={handleChange}
                acks={acks}
                escalations={escalations}
                onSubmit={handleSubmit}
              />
            )}

            {tabIndex === 1 && (
              <TabProcedimento
                hostOverview={hostOverview}
                overviewLoading={overviewLoading}
                props={props}
              />
            )}

            {isWazuh && tabIndex === 2 && (
              <TabWazuh props={props} />
            )}

            {tabIndex === (isWazuh ? 3 : 2) && (
              <TabContatos
                props={props}
                showNotify={showNotify}
              />
            )}

            {tabIndex === (isWazuh ? 4 : 3) && (
              <TabChamado
                props={props}
                state={state}
                setState={setState}
                showNotify={showNotify}
                athenaConfigs={athenaConfigs}
              />
            )}
          </DialogContent>

          <DialogActions>
            <Button sx={{ color: theme.palette.text.primary }} onClick={handlerClose}>Cancelar</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
}

const Snack = (props) => {
  return (
    <Snackbar
      open={props.open}
      autoHideDuration={6000}
      onClose={() => { props.handlerSnack(); }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <Alert onClose={() => { props.handlerSnack(); }} severity={props.severity ? props.severity : 'error'} sx={{ width: '100%' }}>
        {props.message}
      </Alert>
    </Snackbar>
  );
};
